/**
 * 이상한 방 - RoomState 와이어 변환
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 2절(스냅샷 두 벌) · 2.1절(`placedBy`는 서버 전용) ·
 *            11.2절(클라이언트가 두 방을 가진다)
 *
 * ## 왜 필요한가
 *
 * `RoomState.objects`가 `ReadonlyMap`이라 `JSON.stringify`가 `{}`를 낸다. 방을 소켓에 실으려면
 * 배열로 펴야 하고, 그 펴는 규칙이 두 곳(서버·루프백)에 따로 있으면 한쪽만 고쳐진다.
 *
 * ## 세 가지 결정
 *
 *  1. **`placedBy`를 벗긴다.** 2.1절이 서버 전용으로 못박은 필드다 - 체인저의 심리전 재료이므로
 *     어떤 페이즈에서도 클라이언트로 나가지 않는다. 복원할 때는 `WIRE_PLACER` 상수를 채운다.
 *     `ObjectInstance`가 그 필드를 요구하는데 와이어에는 없으니 **무엇으로든 채워야** 하고,
 *     빈 문자열 대신 이름 있는 상수를 쓰는 이유는 `countByPlayer`류 조회가 우연히 어떤
 *     실제 플레이어와 일치하는 사고를 막기 위해서다.
 *  2. **id 오름차순으로 정렬한다.** Map의 삽입 순서는 생성 경로의 우연이다(생성기의 배치 순서·
 *     동사 적용 순서). 그 순서를 와이어에 그대로 실으면 같은 방이 다른 바이트열이 되어
 *     스냅샷 비교·해시·재접속 대조가 전부 흔들린다. 정렬은 "같은 방 = 같은 와이어"를 만든다.
 *     정렬 뒤 복원하면 Map 순서도 정렬 순서가 되므로 **왕복이 순서까지 보존**한다.
 *  3. **복원은 id 중복만 검사한다.** 2절이 id 재사용을 금지하고 `addObject`가 같은 이유로 던진다.
 *     형태 검증(rot 범위·셀 정수 여부)은 프로토콜 층의 몫이다 - 여기서 다 하면 두 층이 같은
 *     검사를 갖게 되고, 그중 하나만 갱신된다.
 *
 * ## 참조를 공유하지 않는다
 *
 * `cell`을 양쪽 변환에서 얕게 복사한다. 소켓을 지나면 JSON이 어차피 끊지만, 루프백(같은 프로세스 안의
 * 호스트 ↔ 클라이언트)은 JSON을 안 거칠 수 있고, 그때 복원된 방과 원본 방이 같은 `Cell3`를 들면
 * "두 스냅샷이 독립이다"(2절)가 타입으로만 참이 된다. 코덱 단독으로 참조를 끊어 두면 루프백이
 * `JSON.stringify`를 끼웠는지에 안전이 걸리지 않는다.
 *
 * 순수 함수다. `core/`의 다른 파일과 마찬가지로 위 레이어를 참조하지 않는다.
 */

import { compareCodePoint } from './order'
import type { Cell3, InstanceId, ObjectInstance, PlayerId, RoomState } from './types'

/* --- 타입 ------------------------------------------------------------------- */

/** 와이어에 실리는 오브젝트. `ObjectInstance`에서 `placedBy`만 뺀 것이다(2.1절). */
export type WireObject = Readonly<Omit<ObjectInstance, 'placedBy'>>

/** 와이어에 실리는 방. `objects`는 id 오름차순 배열이다. */
export type WireRoom = Readonly<{
  templateId: string
  objects: readonly WireObject[]
}>

/* --- 상수 ------------------------------------------------------------------- */

/**
 * 복원된 오브젝트의 `placedBy`. 와이어에는 배치자가 없으므로 이 값이 "모른다"의 표기다.
 *
 * 실제 플레이어 id와 겹치지 않아야 `countByPlayer`류 조회가 우연히 어떤 사람과 일치하지 않는다.
 * 그 보장은 이 파일이 아니라 **id를 발급하는 쪽**(서버 마일스톤의 `identity.ts`)의 몫이다 -
 * 그 파일은 콜론 없는 id만 발급해야 하고, 그 파일의 테스트가 `expect(id).not.toContain(':')`로
 * 이 상수와 충돌하지 않음을 단언한다. 지금(`PlayerId = string`) 이를 지키는 코드는 없다.
 */
export const WIRE_PLACER: PlayerId = 'wire:unknown'

/* --- 변환 ------------------------------------------------------------------- */

/**
 * 오브젝트 하나에서 `placedBy`를 벗긴다(2.1절).
 *
 * 방 전체가 아니라 한 건이 와이어를 지나는 자리가 있다 - 꾸미기의 "누가 무엇을 놓았다"(M10 `placed` 효과)다.
 * 거기서 `encodeRoom`으로 한 물건짜리 방을 만들어 첫 원소를 꺼내는 것은 같은 일을 돌아가는 길이고,
 * 그 길이 있는 한 필드가 하나 늘어나는 날 한쪽만 조용히 낡는다.
 */
export function encodeObject(obj: ObjectInstance): WireObject {
  return {
    id: obj.id,
    catalogId: obj.catalogId,
    cell: copyCell(obj.cell),
    rot: obj.rot,
    colorId: obj.colorId,
  }
}

/** 와이어 오브젝트를 방의 것으로 되돌린다. 배치자는 밖에서 온다 - 와이어에는 없기 때문이다. */
export function decodeObject(obj: WireObject, placedBy: PlayerId = WIRE_PLACER): ObjectInstance {
  return {
    id: obj.id,
    catalogId: obj.catalogId,
    cell: copyCell(obj.cell),
    rot: obj.rot,
    colorId: obj.colorId,
    placedBy,
  }
}

/** 방을 와이어 형태로 편다. `placedBy`를 벗기고 id 오름차순으로 정렬한다. */
export function encodeRoom(room: RoomState): WireRoom {
  const objects: WireObject[] = []
  for (const obj of room.objects.values()) objects.push(encodeObject(obj))
  objects.sort(compareId)
  return { templateId: room.templateId, objects }
}

/**
 * 와이어 형태를 방으로 되돌린다. `placedBy`는 `WIRE_PLACER`다.
 * 배열 순서대로 넣으므로 정렬된 와이어에서 복원한 Map은 id 오름차순이다.
 */
export function decodeRoom(wire: WireRoom): RoomState {
  const objects = new Map<InstanceId, ObjectInstance>()
  for (const obj of wire.objects) {
    if (objects.has(obj.id)) {
      throw new Error(`decodeRoom: id 재사용 금지 - 와이어에 InstanceId '${obj.id}'가 두 번 있다`)
    }
    objects.set(obj.id, decodeObject(obj))
  }
  return { templateId: wire.templateId, objects }
}

/** id 순. 로케일 무관 비교자를 쓰는 이유는 `core/order.ts` 머리말. */
function compareId(a: WireObject, b: WireObject): number {
  return compareCodePoint(a.id, b.id)
}

/** 머리말 "참조를 공유하지 않는다". `Cell3`는 세 숫자라 얕은 복사로 끝난다. */
function copyCell(cell: Cell3): Cell3 {
  return { x: cell.x, y: cell.y, z: cell.z }
}
